/* ============================================================
   LIQUID_GOLD: Custom header behavior
   Sticky state, keyboard-accessible dropdowns, and the mobile
   bottom-sheet drawer. All under window.LiquidGold. Load deferred.
   Uses event delegation from the header root — no per-element binding.
   ============================================================ */
window.LiquidGold = window.LiquidGold || {};

window.LiquidGold.header = {
  FOCUSABLE:
    'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])',

  init() {
    const root = document.querySelector('[data-lg-header]');
    if (!root || root.dataset.lgInit === 'true') return;
    root.dataset.lgInit = 'true';
    this.root = root;
    this.initSticky(root);
    this.initDropdowns(root);
    this.initDrawer(root);
  },

  /* ---------- Sticky (stuck blur + hide-on-down / reveal-on-up) ---------- */
  // The header is a fixed overlay (see CSS), so it stays pinned without JS. This
  // only manages the "stuck" blur state and the hide-on-scroll-down /
  // reveal-on-scroll-up transform via .lg-header--hidden.
  initSticky(root) {
    if (!root.classList.contains('lg-header--sticky')) return;

    let ticking = false;
    let lastY = window.scrollY;
    // LIQUID_GOLD: only start hiding after ~one screen of downward scroll
    const DELTA = 6; // dead zone to avoid jitter on tiny scrolls

    const apply = () => {
      const y = window.scrollY;
      const threshold = window.innerHeight;

      // Stuck state (backdrop blur + tighter padding) once past a small offset.
      root.classList.toggle('lg-header--stuck', y > 10);

      if (y <= threshold) {
        // Near the top: always reveal.
        root.classList.remove('lg-header--hidden');
      } else if (y > lastY + DELTA) {
        // Scrolling down past the threshold: hide.
        root.classList.add('lg-header--hidden');
      } else if (y < lastY - DELTA) {
        // Scrolling up: reveal.
        root.classList.remove('lg-header--hidden');
      }

      lastY = y;
      ticking = false;
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      // LIQUID_GOLD: batch reads/writes into a frame to avoid layout thrash
      window.requestAnimationFrame(apply);
    };
    // LIQUID_GOLD: passive listener for scroll perf
    window.addEventListener('scroll', onScroll, { passive: true });
    apply();
  },

  /* ---------- Desktop dropdowns ---------- */
  initDropdowns(root) {
    const closeAll = (except) => {
      root.querySelectorAll('[data-lg-dropdown-toggle][aria-expanded="true"]').forEach((btn) => {
        if (btn !== except) btn.setAttribute('aria-expanded', 'false');
      });
    };

    // Toggle on click (hover is CSS-only).
    root.addEventListener('click', (event) => {
      const toggle = event.target.closest('[data-lg-dropdown-toggle]');
      if (!toggle || !root.contains(toggle)) return;
      event.preventDefault();
      const isOpen = toggle.getAttribute('aria-expanded') === 'true';
      closeAll(toggle);
      toggle.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
    });

    // ESC closes the open dropdown and returns focus to its toggle.
    root.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const openToggle = root.querySelector('[data-lg-dropdown-toggle][aria-expanded="true"]');
      if (openToggle) {
        closeAll(null);
        openToggle.focus();
      }
    });

    // Outside click / focus closes everything.
    document.addEventListener('click', (event) => {
      if (!root.contains(event.target)) closeAll(null);
    });
    document.addEventListener('focusin', (event) => {
      if (!root.contains(event.target)) closeAll(null);
    });
  },

  /* ---------- Mobile drawer ---------- */
  initDrawer(root) {
    // The drawer is a sibling of the <header data-lg-header>, not a descendant,
    // so scope the lookup to the containing section (or the document).
    const scope = root.closest('.lg-header-section') || document;
    const drawer = scope.querySelector('[data-lg-drawer]');
    const trigger = root.querySelector('[data-lg-drawer-open]');
    if (!drawer || !trigger) return;

    const sheet = drawer.querySelector('[data-lg-drawer-sheet]');
    const htmlEl = document.documentElement;

    const onKeydown = (event) => {
      if (event.key === 'Escape') {
        close();
        return;
      }
      if (event.key === 'Tab') this.trapFocus(event, sheet);
    };

    const open = () => {
      drawer.classList.add('lg-drawer--open');
      drawer.setAttribute('aria-hidden', 'false');
      trigger.setAttribute('aria-expanded', 'true');
      htmlEl.classList.add('lg-scroll-locked');
      document.addEventListener('keydown', onKeydown);
      // Move focus to the first focusable element in the sheet.
      const first = sheet.querySelector(this.FOCUSABLE);
      if (first) window.requestAnimationFrame(() => first.focus());
    };

    const close = () => {
      if (!drawer.classList.contains('lg-drawer--open')) return;
      drawer.classList.remove('lg-drawer--open');
      drawer.setAttribute('aria-hidden', 'true');
      trigger.setAttribute('aria-expanded', 'false');
      htmlEl.classList.remove('lg-scroll-locked');
      document.removeEventListener('keydown', onKeydown);
      trigger.focus();
    };

    trigger.addEventListener('click', open);

    drawer.addEventListener('click', (event) => {
      if (event.target.closest('[data-lg-drawer-close]') || event.target.hasAttribute('data-lg-drawer-overlay')) {
        close();
      }
    });
  },

  /* ---------- Focus trap ---------- */
  trapFocus(event, container) {
    const items = Array.from(container.querySelectorAll(this.FOCUSABLE)).filter(
      (el) => el.offsetParent !== null || el === document.activeElement
    );
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => window.LiquidGold.header.init());
} else {
  window.LiquidGold.header.init();
}

// LIQUID_GOLD: re-init inside the Shopify theme editor when the section reloads
document.addEventListener('shopify:section:load', (event) => {
  if (event.target && event.target.querySelector('[data-lg-header]')) {
    window.LiquidGold.header.init();
  }
});
